import {Pagination, Tooltip} from '@mui/material';
import axios from 'axios';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from "react-router-dom";
import {ReactComponent as ArrowCircleRightIcon} from "../../../assets/icons/arrow-circle-right.svg";
import {ReactComponent as EditIcon} from "../../../assets/icons/edit.svg";
import {ReactComponent as FolderPlusIcon} from "../../../assets/icons/folder-plus.svg";
import {ReactComponent as KeyIcon} from "../../../assets/icons/key.svg";
import {ReactComponent as MoreIcon} from "../../../assets/icons/more.svg";
import {ReactComponent as TrashIcon} from "../../../assets/icons/trash.svg";
import {ReactComponent as User2Icon} from "../../../assets/icons/user_2.svg";
import {ReactComponent as PinIcon} from "../../../assets/icons/pin.svg"
import PageSizeSelector from '../../../components/common/PageSizeSelector';
import ResponsiveTable, {ResponsiveTableColumn} from "../../../components/common/ResponsiveTable";
import SearchInput from '../../../components/common/SearchInput';
import {
    ControlsRow,
    PageSizeWrapper,
    PaginationWrapper,
    SearchWrapper,
    StyledTableContainer
} from "../../../components/common/TableContainer.styled";
import CreateDataSourceDialog from "../../../components/data-sources/CreateDataSourceDialog";
import DataSourcePermissionDialog from "../../../components/data-sources/DataSourcePermissionDialog";
import {
    ActionButton,
    ContextMenu,
    ContextMenuItem,
    OptionsButton,
    OwnerInfo
} from "../../../components/data-sources/DataSourceTable.styled";
import GenericInputDialog from "../../../components/dialogs/InputDialog";
import {useSnackbar} from "../../../contexts/SnackbarContext";
import {collectionService} from "../../../services/collection.service";
import {parseError} from "../../../errors/http.service";
import {sortDataSourcesByDefaultFlag} from "../../../utils/format-data-sources";
import {Header, StyledDocumentButton} from './AdminDataSourceList.styled';
import {COLLECTION_TYPE, CollectionDataContent} from "../../../models/collections.models";

const ADMIN_DATA_SOURCE_CURRENT_PAGE = 'adminDataSourceCurrentPage';
const ADMIN_DATA_SOURCE_PAGE_SIZE = 'adminDataSourcePageSize';
const ADMIN_DATA_SOURCE_SEARCH_VALUE = 'adminDataSourceSearchValue';
const SEARCH_DEBOUNCE_MS = 400;

export interface EditDataSourcePermission {
    id: number;
    owner: string
}

// TODO: move axios to single service dedicated to API
const AdminDataSourceList = () => {
    const navigate = useNavigate();
    const {t} = useTranslation();
    const {showSnackbar} = useSnackbar();

    const [loading, setLoading] = useState(true);

    const [dataSources, setDataSources] = useState<CollectionDataContent[]>([]);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [selectedEditDataSourcePermission, setSelectedEditDataSourcePermission] = useState<EditDataSourcePermission | null>(null)
    const [showPermissionDialog, setShowPermissionDialog] = useState(false);
    const [dataSourceToChangeName, setDataSourceToChangeName] = useState<CollectionDataContent | null>(null);
    const [isChangeNameDialogOpen, setIsChangeNameDialogOpen] = useState(false);

    const [searchValue, setSearchValue] = useState(() => {
        try {
            const savedValue = sessionStorage.getItem(ADMIN_DATA_SOURCE_SEARCH_VALUE);
            if (!savedValue) {
                return '';
            }

            const parsedValue = JSON.parse(savedValue);
            return typeof parsedValue === 'string' ? parsedValue : '';
        } catch (e) {
            return '';
        }
    });

    const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);
    const [currentPage, setCurrentPage] = useState(() => {
        try {
            const savedPage = sessionStorage.getItem(ADMIN_DATA_SOURCE_CURRENT_PAGE);
            return savedPage ? parseInt(savedPage, 10) : 1;
        } catch (e) {
            return 1;
        }
    });

    const [pageSize, setPageSize] = useState(() => {
        try {
            const savedPageSize = sessionStorage.getItem(ADMIN_DATA_SOURCE_PAGE_SIZE);
            return savedPageSize ? parseInt(savedPageSize, 10) : 10;
        } catch (e) {
            return 5;
        }
    });

    const [totalElements, setTotalElements] = useState(0);
    const pageSizeOptions = [5, 10, 20, 50];

    useEffect(() => {
        const normalizedSearchValue = searchValue.trim();
        const normalizedDebouncedSearchValue = debouncedSearchValue.trim();

        if (normalizedSearchValue === normalizedDebouncedSearchValue) {
            return;
        }

        const timeoutId = setTimeout(() => {
            setDebouncedSearchValue(searchValue);
            sessionStorage.setItem(ADMIN_DATA_SOURCE_CURRENT_PAGE, '1');
            setCurrentPage(1);
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timeoutId);
    }, [debouncedSearchValue, searchValue]);

    // TODO: add endpoint with searchTerm and add here as a service endpoint
    const fetchDataSources = useCallback(async () => {
        setLoading(true);
        try {
            const normalizedSearchValue = debouncedSearchValue.trim();

            if (normalizedSearchValue) {
                const params = new URLSearchParams();
                params.append('page', (currentPage - 1).toString());
                params.append('size', pageSize.toString());
                params.append('name', normalizedSearchValue);

                const response = await axios.get('/documents/by-collection', {params});

                setDataSources(response.data.content);
                setTotalElements(response.data.totalElements || 0);
            } else {
                collectionService.invalidateCache();
                const data = await collectionService.fetchCollections(currentPage - 1, pageSize);

                setDataSources(data?.content ?? []);
                setTotalElements(data?.totalElements ?? 0);
            }
        } catch (error) {
            console.error('Error fetching data sources:', error);
            setDataSources([]);
            setTotalElements(0);
        } finally {
            setLoading(false);
        }
    }, [currentPage, debouncedSearchValue, pageSize]);

    useEffect(() => {
        void fetchDataSources();
    }, [fetchDataSources]);

    const handleSearchChange = (value: string) => {
        sessionStorage.setItem(ADMIN_DATA_SOURCE_SEARCH_VALUE, JSON.stringify(value));
        setSearchValue(value);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        sessionStorage.setItem(ADMIN_DATA_SOURCE_CURRENT_PAGE, value.toString());
        setCurrentPage(value);
    };

    const handlePageSizeChange = (size: number) => {
        sessionStorage.setItem(ADMIN_DATA_SOURCE_PAGE_SIZE, size.toString());
        setPageSize(size);
        sessionStorage.setItem(ADMIN_DATA_SOURCE_CURRENT_PAGE, '1');
        setCurrentPage(1);
    };

    const handleDelete = async (collectionId: number) => {
        try {
            await collectionService.deleteSource(collectionId);
            setDataSources(dataSources.filter(c => c.id !== collectionId));
        } catch (error) {
            showSnackbar(parseError(error), "error");
        }
    };

    const openChangeNameDialog = (dataSourceId: number) => {
        const dataSourceToSet = dataSources.find(c => c.id === dataSourceId);
        if (dataSourceToSet) {
            setDataSourceToChangeName(dataSourceToSet);
            setIsChangeNameDialogOpen(true);
        }
    };

    const confirmChangeNameDataSource = async (dataSourceName: string) => {
        if (!dataSourceToChangeName) return;
        try {
            await collectionService.updateSourceName(dataSourceToChangeName.id, dataSourceName);
            await fetchDataSources();
        } catch (error) {
            showSnackbar(parseError(error), "error");
        } finally {
            setIsChangeNameDialogOpen(false);
            setDataSourceToChangeName(null);
        }
    };

    const handleManagePermissions = (editDataSourcePermission: EditDataSourcePermission) => {
        setSelectedEditDataSourcePermission(editDataSourcePermission);
        setShowPermissionDialog(true);
    }

    const handleDataSourcePreview = useCallback((dataSourceId: number) => {
        navigate(`/data-sources/${dataSourceId}/documents`, {
            state: {
                backPath: '/admin/data-sources/list',
            }
        });
    }, [navigate])

    const handleDataSourceCreated = (dataSourceId: number) => {
        navigate(`/data-sources/${dataSourceId}/documents`, {
            state: {
                backPath: '/admin/data-sources/list',
            }
        });
    };

    const handleSetCollectionDefaultFlag = async (dataSourceId: number, isDefault: boolean) => {
        try {
            await collectionService.updateCollectionDefaultFlag(dataSourceId, isDefault);
            showSnackbar(t("dataSourceTable.message.updateCollectionSuccess"), "success");
            await fetchDataSources();
        } catch (error) {
            console.error(error);
            showSnackbar(t("dataSourceTable.message.updateCollectionError"), "error");
        }
    }

    const translateCollectionType = React.useCallback((type: string): string => {
        switch (type) {
            case COLLECTION_TYPE.MANUAL:
                return t('dataSourceTable.collectionType.manual');
            case COLLECTION_TYPE.AUTOMATIC:
                return t('dataSourceTable.collectionType.automatic');
            case COLLECTION_TYPE.DEFAULT:
                return t('dataSourceTable.collectionType.default');
            default:
                return type;
        }
    }, [t]);

    const [contextMenu, setContextMenu] = useState<{
        mouseX?: number;
        mouseY?: number;
        anchorEl?: HTMLElement | null;
        dataSource: CollectionDataContent;
    } | null>(null);

    const handleContextMenu = useCallback((event: React.MouseEvent, dataSource: CollectionDataContent) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            anchorEl: event.currentTarget as HTMLElement,
            dataSource: dataSource,
        });
    }, []);

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handlePreviewClick = React.useCallback((dataSourceId: number, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }
        handleDataSourcePreview(dataSourceId);
    }, [handleDataSourcePreview]);

    const columns: ResponsiveTableColumn<CollectionDataContent>[] = useMemo(() => [
        {
            id: 'collectionDefault',
            header: '',
            render: (ds) => (
                ds.isDefault ? (
                    <Tooltip title={t('dataSourceTable.tooltips.default')}>
                        <span><PinIcon aria-hidden={true} focusable={false} style={{width: '16px', height: '16px'}}/></span>
                    </Tooltip>
                ) : null
            ),
        },
        {
            id: 'name',
            header: t('dataSourceTable.headers.name'),
            render: (ds) => (
                <StyledDocumentButton onClick={(e: React.MouseEvent<HTMLButtonElement>) => handlePreviewClick(ds.id, e)}>
                    {ds.name}
                </StyledDocumentButton>
            ),
        },
        {
            id: 'lastModification',
            header: t('dataSourceTable.headers.lastModification'),
            render: (ds) => new Date(ds.lastModificationDate).toLocaleDateString(),
        },
        {
            id: 'documents',
            header: t('dataSourceTable.headers.documents'),
            render: (ds) => ds.amountOfDocuments,
        },
        {
            id: 'type',
            header: t('dataSourceTable.headers.type'),
            render: (ds) => translateCollectionType(String(ds.collectionType)),
        },
        {
            id: 'actions',
            header: '',
            headerLabel: t('common.actions') as string,
            cellClassName: 'actions-cell',
            render: (ds) => (
                <>
                    <Tooltip title={t('common.actions')}>
                        <ActionButton
                            className="mobile-only"
                            variant="outlined"
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.dataSource.id === ds.id ? `admin-ds-menu-${ds.id}` : undefined}
                            aria-expanded={contextMenu?.dataSource.id === ds.id ? true : undefined}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, ds);
                            }}
                        >
                            {t('adminDataSourceList.actions')}
                        </ActionButton>
                    </Tooltip>
                    <Tooltip title={t('common.actions')}>
                        <OptionsButton
                            className="desktop-only"
                            style={{ width: 26 }}
                            size="small"
                            aria-label={t('common.actions') as string}
                            aria-haspopup="menu"
                            aria-controls={contextMenu?.dataSource.id === ds.id ? `admin-ds-menu-${ds.id}` : undefined}
                            aria-expanded={contextMenu?.dataSource.id === ds.id ? true : undefined}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                e.stopPropagation();
                                handleContextMenu(e, ds);
                            }}
                        >
                            <MoreIcon aria-hidden={true} focusable={false}/>
                        </OptionsButton>
                    </Tooltip>
                </>
            ),
        },
    ], [t, handlePreviewClick, handleContextMenu, contextMenu?.dataSource.id]);

    if (loading && dataSources.length === 0) return <div>Loading...</div>;

    return (
        <StyledTableContainer>
            <Header>
                <h2>{t('adminDataSourceList.title')}</h2>
                <ActionButton
                    variant="contained"
                    onClick={() => setIsCreateDialogOpen(true)}
                    startIcon={<FolderPlusIcon aria-hidden={true} focusable={false}/>}
                                        aria-label={t('adminDataSourceList.createButton') as string}
                                        sx={{backgroundColor: 'primary.main'}}
                >
                    {t('adminDataSourceList.createButton')}
                </ActionButton>
            </Header>

            <ControlsRow>
                <SearchWrapper>
                    <SearchInput
                        onChange={handleSearchChange}
                        value={searchValue}
                        ariaLabel={t('adminDataSourceList.search.ariaLabel') as string}
                    />
                </SearchWrapper>
                <PageSizeWrapper>
                    <PageSizeSelector
                        options={pageSizeOptions}
                        selectedSize={pageSize}
                        onChange={handlePageSizeChange}
                        ariaLabelGroup={t('pageSizeSelector.aria.group') as string}
                        getSizeAriaLabel={(size) => t('pageSizeSelector.aria.setSize', { size }) as string}
                    />
                </PageSizeWrapper>
            </ControlsRow>

            <ResponsiveTable
                columns={columns}
                data={dataSources}
                getRowKey={(ds) => ds.id}
                emptyState={t('adminDataSourceList.emptyState')}
                ariaLabel={t('adminDataSourceList.table.ariaLabel') as string}
            />

            <ContextMenu
                id={contextMenu ? `admin-ds-menu-${contextMenu.dataSource.id}` : undefined}
                open={contextMenu !== null}
                onClose={handleCloseContextMenu}
                anchorEl={contextMenu?.anchorEl ?? null}
                anchorReference={contextMenu?.anchorEl ? 'anchorEl' : 'anchorPosition'}
                anchorPosition={
                    contextMenu && contextMenu.mouseY !== undefined && contextMenu.mouseX !== undefined
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
                MenuListProps={{
                    autoFocusItem: true,
                    'aria-labelledby': contextMenu ? `admin-ds-menu-${contextMenu.dataSource.id}` : undefined,
                }}
            >
                {contextMenu && [
                    contextMenu.dataSource.owner ? (
                        <ContextMenuItem key="owner">
                            <OwnerInfo>
                                <User2Icon aria-hidden={true} focusable={false}/>
                                {contextMenu.dataSource.owner.email}
                            </OwnerInfo>
                        </ContextMenuItem>
                    ) : null,
                    (
                        <ContextMenuItem key="details" onClick={() => {
                            handleCloseContextMenu();
                            handleDataSourcePreview(contextMenu.dataSource.id);
                        }}>
                            <ArrowCircleRightIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.details')}
                        </ContextMenuItem>
                    ),
                    (
                        <ContextMenuItem key="changeName" onClick={() => {
                            handleCloseContextMenu();
                            openChangeNameDialog(contextMenu.dataSource.id);
                        }}>
                            <EditIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.changeName')}
                        </ContextMenuItem>
                    ),
                    (
                        <ContextMenuItem key="permissions" onClick={() => {
                            handleCloseContextMenu();
                            handleManagePermissions({ id: contextMenu.dataSource.id, owner: contextMenu.dataSource.owner?.email || '' });
                        }}>
                            <KeyIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.permissions')}
                        </ContextMenuItem>
                    ),
                    contextMenu.dataSource.isDefault ? null : (
                        <ContextMenuItem key="setDefault" onClick={() => {
                            handleSetCollectionDefaultFlag(contextMenu.dataSource.id, true);
                            handleCloseContextMenu();
                        }}>
                            <PinIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.setAsDefault')}
                        </ContextMenuItem>
                    ),
                    contextMenu.dataSource.isDefault ? (
                        <ContextMenuItem key="unsetDefault" style={{color: '#f44336'}} onClick={() => {
                            handleSetCollectionDefaultFlag(contextMenu.dataSource.id, false);
                            handleCloseContextMenu();
                        }}>
                            <PinIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.unsetAsDefault')}
                        </ContextMenuItem>
                    ) : null,
                    (
                        <ContextMenuItem key="delete"
                            onClick={() => {
                                handleCloseContextMenu();
                                handleDelete(contextMenu.dataSource.id);
                            }}
                            style={{color: '#f44336'}}
                        >
                            <TrashIcon aria-hidden={true} focusable={false} style={{marginRight: '8px', width: '16px', height: '16px'}}/>
                            {t('dataSourceTable.contextMenu.delete')}
                        </ContextMenuItem>
                    ),
                ]}
            </ContextMenu>

            {totalElements > 5 && (
                <PaginationWrapper>
                    <Pagination
                        count={Math.ceil(totalElements / pageSize)}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        aria-label={t('adminDataSourceList.pagination.ariaLabel') as string}
                    />
                </PaginationWrapper>
            )}

            <CreateDataSourceDialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                onDataSourceCreated={handleDataSourceCreated}
            />

            {selectedEditDataSourcePermission && (
                <DataSourcePermissionDialog
                    open={showPermissionDialog}
                    dataSourceId={selectedEditDataSourcePermission.id}
                    owner={selectedEditDataSourcePermission?.owner}
                    onClose={() => setShowPermissionDialog(false)}
                />)}

            {dataSourceToChangeName && (
                <GenericInputDialog
                    open={isChangeNameDialogOpen}
                    title={t('inputDialog.title')}
                    label={t('inputDialog.label')}
                    required
                    initialValue={dataSourceToChangeName.name}
                    onClose={() => setIsChangeNameDialogOpen(false)}
                    onConfirm={(val) => confirmChangeNameDataSource(val)}
                />
            )}

        </StyledTableContainer>
    );
};

export default AdminDataSourceList;
